import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { createEditor, Transforms, Editor, Range } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { Music } from 'lucide-react';
import {
  buildMentionNode,
  prepareMentionAssets,
  getAssetsSignature,
  parseLineToChildren,
  withAutoMentions,
  forceConvertMentions,
} from './slateMentionUtils';

/**
 * Slate.js 富文本编辑器：支持内联气泡显示
 * 用于 NodeConsole 的提示词输入框，@ 提及资产时显示带缩略图的气泡
 */

// 自定义 Mention 节点渲染（气泡）
const MentionElement = ({ attributes, children, element }) => {
  // 判断是否是音频类型（通过 assetType 或 category）
  const isAudio = element.assetType === 'audio' ||
                  ['music', 'sfx', 'voice'].includes(element.category);

  // 🆕 判断当前主题模式（通过 html 的 data-theme 属性）
  const isDarkMode = document.documentElement.getAttribute('data-theme') !== 'light';

  return (
    <span
      {...attributes}
      contentEditable={false}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        // 尺寸全用 em：字号跟随正文（控制台输入框 / 全屏编辑器按字号预设），气泡同步缩放
        gap: '0.3em',
        padding: '0 0.4em 0 0.25em',
        borderRadius: '0.3em',
        background: 'rgba(60, 60, 70, 0.8)',
        border: '1px solid rgba(100, 100, 110, 0.3)',
        fontSize: 'inherit',
        fontWeight: 400,
        lineHeight: 1.25, // 比正文行高略小，含边框也不会撑高所在行
        color: isDarkMode ? '#F8FAFC' : '#1F2937',  // 🆕 深色模式用白色，浅色模式用深灰色
        verticalAlign: 'middle',
        userSelect: 'none',
        cursor: 'default',
      }}
    >
      {isAudio ? (
        // 音频类型：始终显示音乐图标，不显示缩略图
        <Music
          size="1.2em"
          color="#fb923c"
          strokeWidth={1.5}
          style={{ flexShrink: 0 }}
        />
      ) : element.thumbUrl ? (
        // 图像/视频类型：显示缩略图
        <img
          src={element.thumbUrl}
          alt=""
          style={{
            width: '1.2em',
            height: '1.2em',
            borderRadius: '0.2em',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : null}
      {element.title}
      {children}
    </span>
  );
};

// 渲染元素
const Element = (props) => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case 'mention':
      return <MentionElement {...props} />;
    default:
      return <p {...attributes}>{children}</p>;
  }
};

// 渲染叶子节点
const Leaf = ({ attributes, children, leaf }) => {
  return <span {...attributes}>{children}</span>;
};

// 自定义编辑器行为
const withMentions = (editor) => {
  const { isInline, isVoid, insertData } = editor;

  editor.isInline = (element) => {
    return element.type === 'mention' ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return element.type === 'mention' ? true : isVoid(element);
  };

  // 粘贴的文本整段转换，不等光标移开
  editor.insertData = (data) => {
    insertData(data);
    forceConvertMentions(editor);
  };

  return withAutoMentions(editor);
};

/**
 * 将 Slate 值转换为纯文本
 * mention 节点 → @「标题」，text 节点 → 原文
 */
const slateValueToText = (value) => {
  return value
    .map((node) => {
      if (node.children) {
        return node.children
          .map((child) => {
            if (child.type === 'mention') {
              return `@「${child.title}」`;
            }
            return child.text || '';
          })
          .join('');
      }
      return '';
    })
    .join('\n');
};

/**
 * 将纯文本解析为 Slate 富文本结构
 * 支持解析 @「标题」 为 mention 气泡节点
 * @param {string} text - 包含 @「标题」 的纯文本
 * @param {Array} assets - 素材库数组（用于匹配 mention）
 * @returns {Array} Slate 节点数组
 */
export const parseTextToSlate = (text, assets = []) => {
  if (!text || typeof text !== 'string') {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  // 🔧 兼容三种格式：@资产名 / @「资产名」 / 「资产名」；找不到资产的保留为纯文本
  const sortedAssets = prepareMentionAssets(assets);
  return text.split('\n').map((line) => ({
    type: 'paragraph',
    children: parseLineToChildren(line, sortedAssets, { allowBareBracket: true }),
  }));
};

/**
 * 从外部值得到初始 Slate 结构：纯文本是权威，slateValue 只是气泡缓存。
 * slateValue 和纯文本对不上（只改了 prompt 没改 slateValue 的旧路径）时，按纯文本重新解析。
 */
const resolveInitialValue = (externalSlateValue, externalValue, assets) => {
  if (externalSlateValue) {
    try {
      const parsed = typeof externalSlateValue === 'string'
        ? JSON.parse(externalSlateValue)
        : externalSlateValue;
      if (Array.isArray(parsed) && parsed.length && slateValueToText(parsed) === (externalValue || '')) {
        return parsed;
      }
    } catch (e) {
    }
  }
  return parseTextToSlate(externalValue || '', assets);
};

const SlatePromptEditor = ({
  value: externalValue = '',
  slateValue: externalSlateValue,  // 新增：外部传入的 Slate 富文本结构
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  disabled,
  promptRef,
  style,
  className,
  assets = [], // 🆕 素材库：文本里的 @资产名 / @「资产名」 自动转气泡
}) => {
  const editor = useMemo(
    () => withMentions(withHistory(withReact(createEditor()))),
    []
  );

  // 素材按签名缓存：父组件每次渲染都会新建数组，签名不变就不重新匹配
  const assetsSignature = getAssetsSignature(prepareMentionAssets(assets));
  const sortedAssets = useMemo(() => prepareMentionAssets(assets), [assetsSignature]); // eslint-disable-line react-hooks/exhaustive-deps
  editor.mentionAssets = sortedAssets;

  // 用于 useInputProtection 的 DOM ref
  const containerRef = useRef(null);

  // 🔑 优先使用 externalSlateValue（富文本结构），对不上或没有就按纯文本解析（含气泡）
  const [value, setValue] = useState(() => resolveInitialValue(externalSlateValue, externalValue, assets));
  const isFocusedRef = useRef(false); // 🔑 跟踪焦点状态

  // 当外部值变化时同步到编辑器（例如切换节点、打开旧项目）
  // 🔑 关键修复：只有在编辑器失去焦点时才同步外部值
  // 🔑 只监听 externalValue，不监听 value（避免 value 变化时误触发）
  const prevExternalValueRef = useRef(externalValue);
  const prevExternalSlateValueRef = useRef(externalSlateValue);

  useEffect(() => {
    // 调试日志已移除

    if (isFocusedRef.current) {
      return; // 🔑 如果编辑器有焦点，不同步
    }

    // 🔑 slateValue 或纯文本任一变化才同步（自己 onChange 回流的值已在 handleChange 记过，不会重复替换）
    if (externalSlateValue === prevExternalSlateValueRef.current && externalValue === prevExternalValueRef.current) {
      return;
    }
    prevExternalSlateValueRef.current = externalSlateValue;
    prevExternalValueRef.current = externalValue;

    // slateValue 与纯文本一致就用 slateValue（含气泡），否则按纯文本解析 @资产名
    const newValue = resolveInitialValue(externalSlateValue, externalValue, sortedAssets);

    // 🔑 使用 Slate Transforms API 替换所有内容：先删除所有节点，再插入新节点
    Editor.withoutNormalizing(editor, () => {
      for (let i = editor.children.length - 1; i >= 0; i--) {
        Transforms.removeNodes(editor, { at: [i] });
      }
      Transforms.insertNodes(editor, newValue, { at: [0] });
    });
    forceConvertMentions(editor); // 旧 slateValue 里残留的纯文本 @资产名 也转成气泡

    setValue(newValue); // 同步 state
  }, [externalValue, externalSlateValue, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🆕 素材库异步加载晚于编辑器渲染：素材到位后补一次全量转换（编辑中不打扰）
  useEffect(() => {
    if (!isFocusedRef.current) forceConvertMentions(editor);
  }, [sortedAssets, editor]);

  // 添加全局样式来修复 Slate placeholder 和编辑器样式
  useEffect(() => {
    const styleId = 'slate-editor-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Placeholder 样式：绝对定位，不占用行高 */
        [data-slate-editor] [data-slate-placeholder] {
          color: rgba(255, 255, 255, 0.3) !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          pointer-events: none !important;
          user-select: none !important;
          width: 100% !important;
          display: inline-block !important;
        }

        /* 编辑器光标样式 */
        [data-slate-editor] {
          cursor: text !important;
        }

        /* 🔑 修复大白框：完全禁用 Slate 的选区装饰器背景 */
        [data-slate-editor] *::selection {
          background: rgba(96, 165, 250, 0.3) !important;
        }

        /* 🔑 关键：禁用焦点时 Slate 自动添加的背景色 */
        [data-slate-editor] [data-slate-node] {
          background: transparent !important;
        }

        [data-slate-editor] [data-slate-leaf] {
          background: transparent !important;
        }

        [data-slate-editor] [data-slate-string] {
          background: transparent !important;
        }

        /* 禁用所有可能的装饰器背景 */
        [data-slate-editor] span[data-slate-leaf="true"] {
          background: transparent !important;
        }

        /* Slate 段落不要有额外的 margin */
        [data-slate-editor] > div[data-slate-node="element"] {
          margin: 0 !important;
          padding: 0 !important;
        }

        /* 段落内部也不要 margin */
        [data-slate-editor] p {
          margin: 0 !important;
          padding: 0 !important;
        }

        /* 🎨 滚动条样式：根据深色/浅色模式自动变化 */
        /* 浅色模式：深色滚动条 */
        [data-slate-editor]::-webkit-scrollbar {
          width: 8px;
        }
        [data-slate-editor]::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
        }
        [data-slate-editor]::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
        [data-slate-editor]::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }

        /* 深色模式：浅色滚动条 */
        .dark [data-slate-editor]::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }
        .dark [data-slate-editor]::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
        }
        .dark [data-slate-editor]::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleChange = useCallback((newValue) => {
    // 更新内部状态
    setValue(newValue);
    // 转换为纯文本并通知外部
    const text = slateValueToText(newValue);
    // 🔑 失焦时的程序性同步（外部值写入 / 素材到位补转）文本没变就不回写：
    //    全屏编辑时背后的控制台输入框也在同步，回写会和全屏的输入抢着写 data.prompt
    if (!isFocusedRef.current && text === prevExternalValueRef.current) return;
    const slateJson = JSON.stringify(newValue);  // 保存完整的 Slate 结构
    // 🔑 记下自己发出的值：父组件回传同样的值时不再整体替换（避免光标跳动/重复归一化）
    prevExternalValueRef.current = text;
    prevExternalSlateValueRef.current = slateJson;
    // 🔑 同时传递纯文本和富文本结构
    onChange({
      target: {
        value: text,
        slateValue: slateJson
      }
    });
  }, [onChange]);

  const handleKeyDown = useCallback((event) => {
    // 将 Slate 的 keydown 事件传递给父组件（用于触发 @ 下拉菜单）
    if (onKeyDown) {
      onKeyDown(event);
    }
  }, [onKeyDown]);

  const handleFocus = useCallback((event) => {
    isFocusedRef.current = true; // 🔑 标记为有焦点
    if (onFocus) {
      onFocus(event);
    }
  }, [onFocus]);

  const handleBlur = useCallback((event) => {
    isFocusedRef.current = false; // 🔑 标记为失去焦点
    forceConvertMentions(editor); // 光标停在 @资产名 末尾时打字阶段没转，失焦补转
    if (onBlur) {
      onBlur(event);
    }
  }, [onBlur, editor]);

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  // 插入 mention（从外部调用）
  const insertMention = useCallback((asset) => {
    if (!asset) return;

    // 删除光标前的 @query
    const { selection } = editor;
    if (selection) {
      const [start] = Range.edges(selection);
      const lineStart = Editor.before(editor, start, { unit: 'line' });
      const lineRange = lineStart ? Editor.range(editor, lineStart, start) : null;

      if (lineRange) {
        const lineText = Editor.string(editor, lineRange);
        const match = lineText.match(/@([^@\s]*)$/);

        if (match) {
          const matchLength = match[0].length;
          const deleteRange = {
            anchor: { ...start, offset: start.offset - matchLength },
            focus: start
          };
          Transforms.delete(editor, { at: deleteRange });
        }
      }
    }

    Transforms.insertNodes(editor, buildMentionNode(asset));
    Transforms.move(editor);
  }, [editor]);

  // 光标移到末尾并聚焦（全屏编辑器打开时用，代替 textarea 的 autoFocus）
  const focusEnd = useCallback(() => {
    try {
      Transforms.select(editor, Editor.end(editor, []));
      ReactEditor.focus(editor);
    } catch (e) {
    }
  }, [editor]);

  // 整体替换内容（语音输入用）：聚焦时外部值不会同步进来，只能直接写编辑器，onChange 照常通知父组件
  const setText = useCallback((text) => {
    const newValue = parseTextToSlate(text || '', editor.mentionAssets);
    Editor.withoutNormalizing(editor, () => {
      for (let i = editor.children.length - 1; i >= 0; i--) {
        Transforms.removeNodes(editor, { at: [i] });
      }
      Transforms.insertNodes(editor, newValue, { at: [0] });
    });
    forceConvertMentions(editor);
    Transforms.select(editor, Editor.end(editor, []));
  }, [editor]);

  // 🆕 获取 Slate 光标位置在纯文本中的偏移量（给 @ 下拉用）
  const getCursorPosition = useCallback(() => {
    const { selection } = editor;
    if (!selection) return null;

    const anchorPath = selection.anchor.path;
    const anchorOffset = selection.anchor.offset;
    let offset = 0;

    try {
      for (let pIdx = 0; pIdx < editor.children.length; pIdx++) {
        const paragraph = editor.children[pIdx];
        if (pIdx < anchorPath[0]) {
          // 光标在后面的 paragraph，累计当前整段纯文本长度
          for (const child of paragraph.children) {
            if (Text.isText(child)) {
              offset += child.text.length;
            } else if (child.type === 'mention') {
              const title = child.title || '';
              offset += `@「${title}」`.length;
            }
          }
          offset += 1; // 换行符
        } else if (pIdx === anchorPath[0]) {
          // 光标在当前 paragraph 内部
          for (let cIdx = 0; cIdx < paragraph.children.length; cIdx++) {
            const child = paragraph.children[cIdx];
            if (cIdx < anchorPath[1]) {
              if (Text.isText(child)) {
                offset += child.text.length;
              } else if (child.type === 'mention') {
                const title = child.title || '';
                offset += `@「${title}」`.length;
              }
            } else if (cIdx === anchorPath[1]) {
              offset += anchorOffset;
              break;
            }
          }
          break;
        }
      }
      return offset;
    } catch (err) {
      console.warn('[getCursorPosition] 计算失败:', err);
      return null;
    }
  }, [editor]);

  // 暴露方法和 DOM ref 给父组件
  useEffect(() => {
    if (promptRef) {
      // 暴露 containerRef（DOM 元素）供 useInputProtection 使用
      promptRef.current = containerRef.current;
      // 额外暴露方法和 editor 实例
      promptRef.current.insertMention = insertMention;
      promptRef.current.focus = () => ReactEditor.focus(editor);
      promptRef.current.blur = () => ReactEditor.blur(editor);
      promptRef.current.focusEnd = focusEnd;
      promptRef.current.setText = setText;
      promptRef.current.getCursorPosition = getCursorPosition; // 🆕
      promptRef.current.editor = editor; // 🔑 暴露 editor 实例供父组件访问光标位置
    }
  }, [promptRef, insertMention, focusEnd, setText, getCursorPosition, editor]);

  return (
    <Slate editor={editor} initialValue={value} value={value} onChange={handleChange}>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          ...style,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // 父容器不滚动，让内部 Editable 滚动
        }}
      >
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          readOnly={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{
            minHeight: style?.minHeight || '84px',
            maxHeight: style?.maxHeight || '220px',
            overflowY: 'auto',
            outline: 'none',
            background: 'transparent',
            flex: 1,
          }}
          className={className}
        />
      </div>
    </Slate>
  );
};

export default SlatePromptEditor;
